import __INVESTMENT_DATA__ from './installment_plans.json';

// ---------- helpers: derive product kind flags from JSON ----------
function deriveKind(p) {
  const isDiscount =
    p.payment_terms?.discount_percent_above_threshold !== undefined ||
    p.slug?.includes('discount') ||
    p.name?.toLowerCase()?.includes('discount');

  const isRentToOwn = p.payment_terms?.rent_to_own === true;

  // Treat residential non-RTO with explicit down payment as "home_purchase-like"
  const isHomePurchase =
    p.category === 'residential' &&
    !isRentToOwn &&
    (p.payment_terms?.down_payment_percent !== undefined ||
      p.payment_terms?.down_payment_percent_min !== undefined);

  // A general investment if it advertises ROI/target gain or is commercial plan
  const hasTargetROI = p.roi?.target_total_gain_percent != null;
  const isGeneralInvestment =
    !isDiscount &&
    !isRentToOwn &&
    (hasTargetROI || p.category === 'commercial');

  return { isDiscount, isRentToOwn, isHomePurchase, isGeneralInvestment };
}

function getDownPaymentPercent(p) {
  return (
    p.payment_terms?.down_payment_percent ??
    p.payment_terms?.down_payment_percent_min ??
    undefined
  );
}

function getMinMonthly(p) {
  return p.payment_terms?.min_monthly_installment?.amount;
}

// ---------- routing preferences ----------
function applyRoutingPreferences(intent, key, rules) {
  const prefers = [];
  const filters = rules?.product_filters || [];
  for (const r of filters) {
    const when = r.when || {};
    const intentOk = when.intent ? when.intent === intent : true;

    const dpMaxOk =
      when.max_downpayment_percent !== undefined
        ? key.downPaymentPercentMax !== undefined &&
          key.downPaymentPercentMax <= when.max_downpayment_percent
        : true;

    const dpMinOk =
      when.min_downpayment_percent !== undefined
        ? key.downPaymentPercentMin !== undefined &&
          key.downPaymentPercentMin >= when.min_downpayment_percent
        : true;

    if (intentOk && dpMaxOk && dpMinOk) prefers.push(...(r.prefer || []));
  }
  return Array.from(new Set(prefers));
}

// ---------- hard filters ----------
function productMatchesQuery(p, key) {
  // Strong selection by planId (id or slug) -> bypass other constraints
  if (key.planId) {
    const wanted = String(key.planId).toLowerCase();
    const idHit = String(p.id).toLowerCase() === wanted;
    const slugHit = (p.slug || '').toLowerCase() === wanted;
    if (!(idHit || slugHit)) return false;
    return true;
  }

  // Project filter
  if (key.projectIds?.length) {
    const ok = p.applicable_projects?.some((pid) =>
      key.projectIds.includes(pid)
    );
    if (!ok) return false;
  }

  // Category filter (optional)
  if (key.category && p.category !== key.category) return false;

  // productKind alignment (optional) — we map to derived kinds
  if (key.productKind) {
    const { isDiscount, isRentToOwn, isHomePurchase, isGeneralInvestment } =
      deriveKind(p);
    const kindMap = {
      discount_offer: isDiscount,
      rent_to_own: isRentToOwn,
      home_purchase: isHomePurchase,
      investment: isGeneralInvestment || p.type === 'investment',
    };
    if (!kindMap[key.productKind]) return false;
  }

  // Down payment constraints
  const dp = getDownPaymentPercent(p);
  if (
    key.downPaymentPercentMax !== undefined &&
    dp !== undefined &&
    dp > key.downPaymentPercentMax
  )
    return false;
  if (
    key.downPaymentPercentMin !== undefined &&
    dp !== undefined &&
    dp < key.downPaymentPercentMin
  )
    return false;

  // Monthly budget (if defined by plan)
  const monthly = getMinMonthly(p);
  if (key.budgetMonthlyMax && !monthly) return null;
  if (key.budgetMonthlyMin && !monthly) return null;
  if (
    key.budgetMonthlyMax !== undefined &&
    // monthly !== undefined &&
    monthly > key.budgetMonthlyMax
  )
    return false;
  if (
    key.budgetMonthlyMin !== undefined &&
    // monthly !== undefined &&
    monthly < key.budgetMonthlyMin
  )
    return false;

  // Tenure
  const tMin = p.payment_terms?.tenure_months_min;
  const tMax = p.payment_terms?.tenure_months_max;
  console.log({
    tMax,
    tMin,
    mx: key.tenureMonthsMin,
    min: key.tenureMonthsMax,
  });
  if (key.tenureMonthsMin && !tMax) return null;
  if (key.tenureMonthsMax && !tMin) return null;
  if (/* key.tenureMonthsMin && tMax &&  */ tMax < key.tenureMonthsMin)
    return false;
  if (/* key.tenureMonthsMax && tMin &&  */ tMin > key.tenureMonthsMax)
    return false;

  // Yield (targeted total gain)
  const tgt = p.roi?.target_total_gain_percent;
  if (
    key.expectedYieldMin !== undefined &&
    tgt != null &&
    tgt < key.expectedYieldMin
  )
    return false;
  if (
    key.expectedYieldMax !== undefined &&
    tgt != null &&
    tgt > key.expectedYieldMax
  )
    return false;

  return true;
}

// ---------- scoring with sane defaults & intent-aware boosts ----------
function scoreProduct(p, key, rules) {
  const defaultWeights = {
    intent_match: 0.6,
    budget_fit: 0.2,
    downpayment_fit: 0.1,
    project_preference_fit: 0.1,
  };
  const weights = rules?.scoring?.weights
    ? rules.scoring.weights
    : defaultWeights;

  let intent_match = 0;
  let budget_fit = 0;
  let downpayment_fit = 0;
  let project_preference_fit = 0;
  let bonus = 0;

  const { isDiscount, isRentToOwn, isHomePurchase, isGeneralInvestment } =
    deriveKind(p);

  // Intent vs derived kind
  if (key.intent === 'invest' && (isGeneralInvestment || isDiscount))
    intent_match = 1;
  if (key.intent === 'buy_home' && isHomePurchase) intent_match = 1;
  if (key.intent === 'rent_to_own' && isRentToOwn) intent_match = 1;
  if (key.intent === 'seek_discount' && isDiscount) intent_match = 1;

  // Budget fit (only if plan defines monthly)
  const monthly = getMinMonthly(p);
  if (monthly !== undefined && key.budgetMonthlyMax !== undefined) {
    budget_fit = monthly <= key.budgetMonthlyMax ? 1 : 0;
  }

  // Downpayment fit
  const dp = getDownPaymentPercent(p);
  if (dp !== undefined) {
    if (
      key.downPaymentPercentMax !== undefined &&
      dp <= key.downPaymentPercentMax
    )
      downpayment_fit = 1;
    if (
      key.downPaymentPercentMin !== undefined &&
      dp >= key.downPaymentPercentMin
    )
      downpayment_fit = Math.max(downpayment_fit, 1);
  }

  // Project preference
  if (key.projectIds?.length) {
    const ok = p.applicable_projects?.some((pid) =>
      key.projectIds.includes(pid)
    );
    project_preference_fit = ok ? 1 : 0;
  }

  // Intent-aware bonuses (tie-breakers baked in)
  const tgtROI = p.roi?.target_total_gain_percent ?? null;
  if (key.intent === 'invest' && tgtROI != null)
    bonus += Math.min(tgtROI / 100, 0.3); // up to +0.3 for higher ROI
  if (key.intent === 'buy_home' && dp != null)
    bonus += dp <= 10 ? 0.2 : dp <= 20 ? 0.1 : 0; // favor lower DP
  if (key.intent === 'rent_to_own' && isRentToOwn) bonus += 0.2;
  if (key.intent === 'seek_discount' && isDiscount) {
    const disc = p.payment_terms?.discount_percent_above_threshold ?? 0;
    bonus += Math.min(disc / 40, 0.25); // e.g., 8% → +0.2 (capped)
  }

  // Base score
  let score =
    weights.intent_match * intent_match +
    weights.budget_fit * budget_fit +
    weights.downpayment_fit * downpayment_fit +
    weights.project_preference_fit * project_preference_fit +
    bonus;

  // Soft boost for “preferProducts”
  if (key.preferProducts?.includes(p.id)) score += 0.15;

  return score;
}

export async function filterInvestments(key) {
  const data = __INVESTMENT_DATA__;
  const { products, routing_rules } = data;

  // prefer list from rules
  // const preferProducts = applyRoutingPreferences(
  //   key.intent,
  //   key,
  //   routing_rules
  // );
  // key.preferProducts = Array.from(
  //   new Set([...(key.preferProducts || []), ...preferProducts])
  // );

  // Filter
  const matched = products.filter((p) => productMatchesQuery(p, key));

  // Score & sort (stable tie-breakers)
  const scored = matched
    .map((p, idx) => ({
      product: p,
      score: scoreProduct(p, key, routing_rules),
      idx,
    }))
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      // Tie-break: intent-specific
      const aMon = getMinMonthly(a.product);
      const bMon = getMinMonthly(b.product);
      const aDP = getDownPaymentPercent(a.product) ?? Infinity;
      const bDP = getDownPaymentPercent(b.product) ?? Infinity;
      const aROI = a.product.roi?.target_total_gain_percent ?? -Infinity;
      const bROI = b.product.roi?.target_total_gain_percent ?? -Infinity;

      if (key.intent === 'invest') {
        if (bROI !== aROI) return bROI - aROI; // higher ROI first
        if (aMon != null && bMon != null && aMon !== bMon) return aMon - bMon; // lower monthly next
      } else if (key.intent === 'buy_home') {
        if (aDP !== bDP) return aDP - bDP; // lower DP first
        if (aMon != null && bMon != null && aMon !== bMon) return aMon - bMon;
      } else if (key.intent === 'rent_to_own') {
        const aR = a.product.payment_terms?.rent_to_own ? 1 : 0;
        const bR = b.product.payment_terms?.rent_to_own ? 1 : 0;
        if (bR !== aR) return bR - aR; // true first
      } else if (key.intent === 'seek_discount') {
        const aDisc =
          a.product.payment_terms?.discount_percent_above_threshold ?? 0;
        const bDisc =
          b.product.payment_terms?.discount_percent_above_threshold ?? 0;
        if (bDisc !== aDisc) return bDisc - aDisc; // larger discount first
      }
      return a.idx - b.idx; // stable
    })
    .map((s) => s.product);

  return { plans: scored, count: scored.length };
}

export const filterProjects = (applicableProjects) => {
  const matched = __INVESTMENT_DATA__.projects.filter((proj) =>
    applicableProjects.includes(proj.id)
  );
  return matched;
};

export default filterInvestments;
